<template>
    <div class="rsv2_info_pop_right">
        <div class="rsv2_info_pop_title">{{ $t('popup_user_reservation.title_appointment_details') }}</div>
        <div class="rsv_rsv_info">
            <div class="rsv_default_form">
                <DatePicker :title="$t('popup_user_reservation.label_appointment_date')" v-model="dateString" />
                <TimePicker :title="$t('popup_user_reservation.label_start_time')" v-model="reservation.startedAt"
                    :disabled="false" :minuteInterval="timeInterval" :timeMin="timeMin" :timeMax="timeMax" />
                <TimePicker :title="$t('popup_user_reservation.label_end_time')" v-model="reservation.endedAt"
                    :disabled="false" :minuteInterval="timeInterval" :timeMin="timeMin" :timeMax="endOfDay" />
                <div class="item">
                    <div class="title_s">{{ $t('popup_user_reservation.label_department_short') }}</div>
                    <div class="form_s">
                        <select class="input_xs" v-model="reservation.groupID">
                            <option v-for="(g, index) in groups" :key="index" :value="g._id">
                                {{ cutString(g.title) }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="item">
                    <div class="title_s">{{ $t('popup_user_reservation.label_staff') }}</div>
                    <div class="form_s">
                        <select class="input_xs" v-model="reservation.columnID">
                            <option v-for="(c, index) in columns" :key="index" :value="c._id">
                                {{ cutString(c.title) }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="item">
                    <div class="title_s">{{ $t('popup_user_reservation.label_program') }}</div>
                    <div class="form_s">
                        <select class="input_xs" v-model="reservation.programID">
                            <option v-for="(p, index) in refinedPrograms" :key="index" :value="p._id" :title="p.title">
                                {{ cutString(p.title) }}
                            </option>
                        </select>
                    </div>
                </div>
                <StatePicker v-model="reservation.statusID" :states="states" />
            </div>
            <small class="red_txt">{{ errorMessage }}</small>
            <hr />
            <div class="title_s">{{ $t('popup_user_reservation.label_appointment_memo') }}</div>
            <textarea class="w_full mgt_5" :placeholder="$t('popup_user_reservation.placeholder_appointment_memo')"
                style="height: 235px" v-model="reservation.note" maxlength="5000"></textarea>
            <div class="btn_wrap right">
                <button class="btn_m btn btn_white mgr_auto" v-if="!isCreate" @click="action_Remove">{{
                    $t('popup_user_reservation.button_delete_appointment') }}</button>
                <button class="btn_m btn btn_dgray" @click="action_Close">{{ $t('common.close') }}</button>
                <button class="btn_m btn btn_blue" :disabled="!canSave" @click="action_Save">{{ $t('common.save')
                }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import { typeToString } from '@/util/type-to-string'
import moment from '@/util/MPMoment'
import Vue from 'vue'

import DatePicker from './Components/DatePicker'
import TimePicker from './Components/TimePicker'
import StatePicker from './Components/StatePicker'

import { closeAlertPopup } from '../../../../sheet-alert'
import { closePopup } from '../../../../sheet-overlays'
import { TimeSelectModeType, clearSaaSTimeSelectMode, reservationSelection, setSelectedReservation } from '../../../../reservation-selection'
import { sheetAxis } from '../../../../sheet-axis'
import { sheetFilters } from '../../../../sheet-filters'
import { createReservation, createRegistReservation, createUnregisteredReservation, updateReservation, removeReservation } from '../../../../reservation-actions'
import { editGuardState, invokeGuard } from '../popup-edit-guard'
import { ipcRenderer } from 'electron'
import { useQueryClient } from '@tanstack/vue-query'
import { isApiErrorWithCode } from '@/apis/instances/check-api-response'
import { v2WorkHolidayFindDateQueryOptions } from '@/composables/queries/v2workholiday/use-v2-workholiday-find-date-query'
import { v2ReservationBlockFindQueryOptions } from '@/composables/queries/v2reservationblock/use-v2-reservationblock-find-query'
import { v2WorkRecordFindDateQueryOptions } from '@/composables/queries/v2workrecord/use-v2-workrecord-find-date-query'

export default {
    setup() {
        const queryClient = useQueryClient()
        return { queryClient }
    },
    components: { StatePicker, TimePicker, DatePicker },
    props: {},
    data() {
        return {
            reservationSelection,
            sheetAxis,
            sheetFilters,
            editGuard: editGuardState,

            originReservation: undefined,
            reservation: {},
            dateString: '',
            programs: [],

            errorMessage: '',
            isLoading: false
        }
    },
    watch: {
        'reservationSelection.selectedReservation': {
            immediate: true,
            deep: true,
            handler: function (newValue, oldValue) {
                if (newValue != oldValue) {
                    this.initData()
                }
            }
        },
        reservationDateString: function (newValue) {
            this.dateString = newValue.toString()
        },
        dateString: {
            handler: function (newValue) {
                if (newValue == undefined) {
                    return
                }
                let date = moment(newValue, 'YYYY-MM-DD')
                let hour = moment(this.reservation.startedAt).hours()
                let minute = moment(this.reservation.startedAt).minutes()
                this.reservation.startedAt = moment(date.set('hours', hour).set('minutes', minute)).toString()

                hour = moment(this.reservation.endedAt).hours()
                minute = moment(this.reservation.endedAt).minutes()
                this.reservation.endedAt = moment(date.set('hours', hour).set('minutes', minute)).toString()
            }
        },
        refinedPrograms: {
            handler: function (newValue) {
                if ((newValue?.length ?? 0) == 0) {
                    return
                }
                if (this.reservation.programID == undefined) {
                    this.reservation.programID = newValue[0]._id
                } else {
                    if (!newValue.some((p) => p._id == this.reservation.programID)) {
                        this.reservation.programID = newValue[0]._id
                    }
                }
                if (this.reservation.endedAt == undefined) {
                    this.reservation.endedAt = moment(this.reservation.startedAt).add(newValue[0].duration, 'minute')
                }
            }
        },
        'reservation.groupID': function (newValue, oldValue) {
            if (newValue != oldValue) {
                this.$nextTick(() => {
                    if (this.columns.map((c) => c._id).includes(this.reservation.columnID)) {
                        return
                    }
                    this.reservation.columnID = this.columns[0]._id
                })
            }
        },
        'reservation.startedAt': function (newValue) {
            let s = moment(newValue)
            let e = moment(this.reservation.endedAt)
            let duration = this.programDuration
            let predictEndedAt = moment(s).add(duration, 'minutes')

            if (e.isSameOrBefore(predictEndedAt)) {
                this.reservation.endedAt = predictEndedAt.toString()
            }
        },
        'reservation.endedAt': function (newValue) {
            let s = moment(this.reservation.startedAt)
            let e = moment(newValue)
            if (s.isSameOrAfter(e)) {
                let duration = this.programDuration
                let predictStartedAt = moment(e).add(-duration, 'minutes')
                this.reservation.startedAt = predictStartedAt.toString()
            }
        },
        'reservation.programID': {
            immediate: true,
            deep: true,
            handler: function (newValue, oldValue) {
                if (oldValue == undefined) {
                    if (this.reservation.endedAt == undefined) {
                        const programIndex = this.refinedPrograms.findIndex(p => p._id == newValue)
                        let duration = this.refinedPrograms[programIndex]?.duration ?? 0
                        let endedAt = moment(this.reservation.startedAt).add(duration, 'minute')
                        this.reservation.endedAt = endedAt.toString()
                    }
                } else if (newValue != oldValue) {
                    let s = moment(this.reservation.startedAt)
                    let duration = this.programDuration
                    this.reservation.endedAt = moment(s).add(duration, 'minutes').toString()
                }
            }
        }
    },
    computed: {
        timeInterval: function () {
            return this.sheetAxis.selectedTimeInterval
        },
        timeMin: function () {
            let time = this.sheetAxis.timeArray[0].time
            return moment(time)
        },
        timeMax: function () {
            let timeArray = this.sheetAxis.timeArray
            let time = timeArray[timeArray.length - 1].time
            return moment(time)
        },
        endOfDay: function () {
            return moment().endOf('days')
        },
        hservice: function () {
            return this.reservationSelection.selectedHService
        },
        groups: function () {
            return this.sheetFilters.groups.filter((g) => {
                return this.sheetFilters.columns.some((c) => c.groupID == g._id)
            })
        },
        columns: function () {
            return this.sheetFilters.columns.filter((c) => c.groupID == this.reservation.groupID)
        },
        selectedColumn: function () {
            return this.columns.find((c) => this.reservation.columnID == c._id)
        },
        programDuration: function () {
            let programID = this.reservation.programID
            let program = this.refinedPrograms.find((p) => p._id == programID)
            return program?.duration ?? this.sheetAxis.selectedTimeInterval
        },
        refinedPrograms: function () {
            let array = this.selectedColumn?.programs ?? []
            // "미선택" 옵션을 무조건 최상위로
            const defaults = array.filter(p => p.isDefault)
            const others = array.filter(p => !p.isDefault).sort((l, r) => l.sortNum - r.sortNum)

            return [...defaults, ...others]
        },
        states: function () {
            return this.sheetFilters.states
        },
        isCreate: function () {
            return this.reservation._id == undefined
        },
        isSame: function () {
            let os = moment(this.originReservation.startedAt)
            let ns = moment(this.reservation.startedAt)
            if (os.format('YYYY-MM-DD') != ns.format('YYYY-MM-DD')) {
                return false
            }
            if (os.format('YYYY-MM-DD HH:mm') != ns.format('YYYY-MM-DD HH:mm')) {
                return false
            }
            let oe = moment(this.originReservation.endedAt)
            let ne = moment(this.reservation.endedAt)
            if (oe.format('YYYY-MM-DD') != ne.format('YYYY-MM-DD')) {
                return false
            }
            if (oe.format('YYYY-MM-DD HH:mm') != ne.format('YYYY-MM-DD HH:mm')) {
                return false
            }

            if (this.originReservation.groupID._id != this.reservation.groupID) {
                return false
            }
            if (this.originReservation.columnID._id != this.reservation.columnID) {
                return false
            }
            if (this.originReservation.programID._id != this.reservation.programID) {
                return false
            }
            if (this.originReservation.statusID._id != this.reservation.statusID) {
                return false
            }
            if (this.isSameNote == false) {
                return false
            }

            return true
        },
        isSameNote: function () {
            let on = this.originReservation.note ?? ''
            let nn = this.reservation.note ?? ''
            return on == nn
        },
        canSave: function () {
            if (this.isCreate == true) {
                return true
            }
            return this.isSame == false
        },
        reservationDateString: function () {
            return moment(this.reservation.startedAt).format('YYYY-MM-DD')
        },
        isJoin: function () {
            return this.hservice?.lastLogin != undefined
        },
        isClosedService: function () {
            const closeReason = this.hservice.closeReason ?? []
            return closeReason.length > 0
        },
        isUsingKakaoAlrimTalk: function () {
            return this.servicesetting.setting.usingKakaoAlimAndFriendTalkLinkage
        },
        canSendMessage: function () {
            if (this.hservice.linkType === '10000') { return false }
            return (this.isClosedService == false) && (this.isUsingKakaoAlrimTalk == true || this.isJoin == true)
        }
    },
    mounted() {
        this.registerAlert()
    },
    beforeDestroy() {
        this.unregisterAlert()
    },
    methods: {
        // MARK: - Private
        initData() {
            this.originReservation = deepCopy(this.reservationSelection.selectedReservation)
            let obj = deepCopy(this.reservationSelection.selectedReservation)
            obj.startedAt = moment(obj.startedAt).toString()
            obj.statusID = typeToString(obj.statusID) == 'object' ? obj.statusID._id : obj.statusID || this.states[0]?._id
            obj.programID = typeToString(obj.programID) == 'object' ? obj.programID._id : obj.programID
            obj.groupID = typeToString(obj.groupID) == 'object' ? obj.groupID._id : obj.groupID
            obj.columnID = typeToString(obj.columnID) == 'object' ? obj.columnID._id : obj.columnID

            this.reservation = obj
            this.dateString = moment(this.reservation.startedAt).format('YYYY-MM-DD').toString()

            this.loadPrograms()
        },
        registerAlert() {
            this.AlertController.registAlert(this, 'RestrictedReservationAlert', {
                usingAlertTag: true,
                text: '예약금지 시간이 포함되어 있습니다.\n해당 시간에 예약을 등록할까요?'
            })
        },
        unregisterAlert() {
            this.AlertController.unregistAlert(this, 'RestrictedReservationAlert')
        },
        async confirmUserInfoChange() {
            let result = undefined
            if (this.editGuard.isEditBasicInfo) {
                result = await invokeGuard('AlertEditUserBasicInfo')
            } else if (this.editGuard.isUserNoteChanged) {
                await invokeGuard('AlertEditUserNote')
            }
            closeAlertPopup()

            return result != false
        },
        cutString(str) {
            return str.length > 20 ? str.substring(0, 20) + '...' : str
        },
        parseTime(data) {
            let time = moment(this.reservation.startedAt).startOf('minutes')
            let start, end
            if (data.isDaybreak) {
                start = time.clone().set({ hour: 0, minute: 0 })
                end = time.clone().set({ hour: 23, minute: 55 })
            } else {
                let [sh, sm] = data.startTime.split(':')
                let [eh, em] = data.endTime.split(':')
                start = time.clone().set({ hour: sh, minute: sm })
                end = time.clone().set({ hour: eh, minute: em })
            }
            return { start, end }
        },
        // MARK: - Action
        async action_Close() {
            if (!(await this.confirmUserInfoChange())) {
                return
            }

            closePopup()
        },
        async action_Save() {
            if (!(await this.confirmUserInfoChange())) {
                return
            }
            try {
                if (this.isLoading) {
                    return
                }
                this.isLoading = true
                let [isBlock, isHolyDay, isNotWork] = await Promise.all([this.requestFetchReservationBlockList(), this.requestFetchWorkHoliday(), this.requestFetchWorkRecord()])
                if (isBlock || isHolyDay || isNotWork) {
                    let alertResult = await this.AlertController.showAlert(this, 'RestrictedReservationAlert')
                    if (alertResult != 'right') {
                        return
                    }
                }
                this.saveReservation()
            } catch (err) {
                console.log(err)
                this.saveReservation()
            } finally {
                this.errorMessage = ''
                this.isLoading = false
            }
        },
        async action_Remove() {
            if (!(await this.confirmUserInfoChange())) {
                return
            }

            const reservation = deepCopy(this.reservation)

            const needConfirm = reservation.needConfirm
            const fromAi = this.originReservation.columnID.isAi && needConfirm
            const aiMessageType = fromAi ? 'cancel' : undefined

            const canSendMessage = this.canSendMessage
            reservation.hserviceID = this.hservice
            let result = await removeReservation(this.reservation, canSendMessage, aiMessageType)
            if (result == undefined || result.data.code != 0) {
                console.log(result)
                return
            }
            setSelectedReservation(result.data.data)

            closePopup()

            clearSaaSTimeSelectMode()
        },
        async saveReservation() {
            if (this.isCreate == true) {
                await this.requestCreateReservation()
            } else {
                await this.requestUpdateReservation()
            }
        },
        // MARK: - Network
        async loadPrograms() {
            this.programs = deepCopy(this.selectedColumn?.programs) ?? []

            if (!this.programs.some((p) => p._id == this.reservation.programID)) {
                this.reservation.programID = undefined
            }
            if (this.reservation.programID == undefined) {
                if (this.programs[0] == undefined) {
                    return
                }
                let sortedProgram = deepCopy(this.programs)
                sortedProgram.filter(p => !p.isDefault).sort((l, r) => l.sortNum - r.sortNum)
                Vue.set(this.reservation, 'programID', sortedProgram[0]._id)
            }
        },
        async requestCreateReservation() {
            let hospitalID = this.hospital.selectedHospital._id
            let hserviceID = this.reservationSelection.selectedHService._id
            let userID = this.reservationSelection.selectedHService.userID
            let name = this.hservice.name
            let phone = this.hservice.phone

            let result

            this.reservation.hospitalID = hospitalID

            const reservation = deepCopy(this.reservation)
            reservation.hserviceID = this.hservice
            reservation.userID = this.hservice.userID

            if (this.hservice.isUnregisteredUser) {
                this.reservation.name = name
                this.reservation.phone = phone
                this.reservation.isUnregisteredUser = this.hservice.isUnregisteredUser

                result = await createUnregisteredReservation(deepCopy(this.reservation), false)
            } else if (!hserviceID || !userID) {
                let message = this.hospital.selectedHospital.smsResendMessage
                if (this.hospital.selectedHospital.usingResendMessage == false) {
                    message = this.hospital.selectedHospital.smsResendMessageEdit
                }
                message = message.replace(/{{병원명}}/g, this.hospital.selectedHospital.shortName)
                message = message.replace('{{고객명}}', name)

                this.reservation.name = name
                this.reservation.phone = phone
                this.reservation.message = message

                result = await createRegistReservation(deepCopy(reservation), this.canSendMessage)
            } else {
                this.reservation.hserviceID = hserviceID
                this.reservation.userID = userID

                result = await createReservation(deepCopy(reservation), false, this.canSendMessage)
            }
            if (!result || result.data.code != 0) {
                this.errorMessage = result?.data?.message ?? ''
                return
            }
            setSelectedReservation(result.data.data)
            closePopup()

            if (this.reservationSelection.timeSelectModeType == TimeSelectModeType.fromSaaS) {
                //강상우 - type: 'create' 추가 및 doneCreateReservation -> doneUpdateReservation 으로 변경
                let payload = { value: { ...result.data.data, type: 'create' } }

                ipcRenderer.send('Window.Main.doneUpdateReservation', payload)
                clearSaaSTimeSelectMode()
            }
        },
        async requestUpdateReservation() {
            const reservation = deepCopy(this.reservation)
            const needConfirm = reservation.needConfirm
            const fromAi = this.originReservation.columnID.isAi && needConfirm
            const isCancel = (this.states.find(s => s.isCancelStatus)._id === this.reservation.statusID) ? true : false
            let aiMessageType = (this.originReservation.columnID != reservation.columnID && fromAi) ? 'confirm' : undefined
            if (fromAi && isCancel) { aiMessageType = 'cancel' }

            reservation.hserviceID = this.hservice
            let result = await updateReservation(deepCopy(this.reservation), false, this.canSendMessage, aiMessageType)
            if (result.data.code != 0) {
                this.errorMessage = result?.data?.message ?? ''
                return
            }
            setSelectedReservation(result.data.data)
            closePopup()

            //강상우 - 예약 변경 
            // let payload = {value: {...result.data.data, type: 'update'}}

            // ipcRenderer.send('Window.Main.doneUpdateReservation', payload)
            clearSaaSTimeSelectMode()

        },
        async requestFetchReservationBlockList() {
            let params = {
                hospitalID: this.hospital.selectedHospital._id,
                columnID: [this.reservation.columnID],
                date: moment(this.reservation.startedAt).format('YYYY-MM-DD'),
                isRemoved: false
            }
            try {
                const blocks = await this.queryClient.fetchQuery(v2ReservationBlockFindQueryOptions(params))
                return blocks.some((b) => {
                    let { start, end } = this.parseTime(b)
                    return moment(this.reservation.startedAt).isBefore(end) && moment(this.reservation.endedAt).isAfter(start)
                })
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 false
                if (!isApiErrorWithCode(err)) throw err
                return false
            }
        },
        async requestFetchWorkHoliday() {
            let hospitalID = this.hospital.selectedHospital._id
            let date = moment(this.reservation.startedAt).format('YYYY-MM-DD')
            let params = {
                hospitalID: hospitalID,
                startDate: date,
                endDate: date,
                isRemoved: false
            }
            try {
                const holidays = await this.queryClient.fetchQuery(v2WorkHolidayFindDateQueryOptions(params))
                return holidays.length > 0
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 false
                if (!isApiErrorWithCode(err)) throw err
                return false
            }
        },
        async requestFetchWorkRecord() {
            if (!this.selectedColumn.employeeID) {
                return false
            }

            let hospitalID = this.hospital.selectedHospital._id
            let date = moment(this.reservation.startedAt).format('YYYY-MM-DD')
            let params = {
                hospitalID: hospitalID,
                startDate: date,
                endDate: date,
                employeeID: [this.selectedColumn.employeeID],
                isRemoved: false
            }
            let workRecords
            try {
                workRecords = await this.queryClient.fetchQuery(v2WorkRecordFindDateQueryOptions(params))
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 빈 목록으로 취급
                if (!isApiErrorWithCode(err)) throw err
                workRecords = []
            }
            if (workRecords.length > 0) {
                let workCode = workRecords[0].workCodeID
                if (workCode.isDefault) {
                    return true
                }
                let minutesSinceStartOfDay = (time) => {
                    let [hours, minutes] = time.format('HH:mm').split(':').map(Number)
                    return hours * 60 + minutes
                }
                let reservationStartMinutes = minutesSinceStartOfDay(moment(this.reservation.startedAt))
                let reservationEndMinutes = minutesSinceStartOfDay(moment(this.reservation.endedAt))
                let workStartMinutes = minutesSinceStartOfDay(moment(workCode.startedAt))
                let workEndMinutes = minutesSinceStartOfDay(moment(workCode.endedAt))

                return reservationEndMinutes <= workStartMinutes || reservationStartMinutes >= workEndMinutes
            }
            return false
        }
    }
}
</script>
